import { BankTransactionBase, Payment } from '../models';
import { ModelResource } from './resource';

export interface PaymentExportResponse {
  blob: Blob;
  filename: string;
  contentType: 'application/json' | 'text/csv';
}

export interface ValidationErrorDetail {
  payment_id: string;
  error: string;
}

export interface TransactionApproveResult {
  transaction_id: string;
  success: boolean;
  error?: string;
  validation_errors?: ValidationErrorDetail[];
}

export interface BulkApproveResponse {
  results: TransactionApproveResult[];
}

export interface PaymentAllocationSchema {
  amount: number;
  invoice?: string;
  credit_memo?: string;
  adjustment?: string;
}

export interface PaymentCreateSchema {
  amount: number;
  currency: string;
  partner_id: string;
  reference_number?: string;
  allocations: PaymentAllocationSchema[];
}

export interface PaymentBatchUpdateSchema {
  transaction: string;

  payments: {
    id?: string; // if undefined creates a payment, if defined updates it
    amount: number;
    currency: string;
    partner_id: string;
    reference_number?: string;
    notes?: string | null;
    allocations: PaymentAllocationSchema[];
  }[];
}

export class Payments extends ModelResource<Payment, PaymentCreateSchema> {
  model = Payment;

  resourcePath = 'v1/payments';

  async approve(payment: Payment): Promise<Payment> {
    return this.api.post(this.path(payment.id, 'approve')).then((res) => {
      payment.updateFromJson(res.data);
      return payment;
    });
  }

  async unapprove(payment: Payment): Promise<Payment> {
    return this.api.post(this.path(payment.id, 'unapprove')).then((res) => {
      payment.updateFromJson(res.data);
      return payment;
    });
  }

  // batch approve all payments related to txn
  async batchApprove(txn: BankTransactionBase): Promise<Payment[]> {
    return this.api
      .post(this.path('batch', 'approve'), {
        data: { transaction_id: txn.id },
      })
      .then((res) => {
        return res.data.payments.map((p) => Payment.fromJson(p));
      });
  }

  // batch unapprove all payments related to txn (symmetrical of batchApprove)
  async batchUnapprove(txn: BankTransactionBase): Promise<Payment[]> {
    return this.api
      .post(this.path('batch', 'unapprove'), {
        data: { transaction_id: txn.id },
      })
      .then((res) => {
        return res.data.payments.map((p) => Payment.fromJson(p));
      });
  }

  // bulk approve payments for multiple transactions (v2 endpoint)
  async bulkApprove(transaction_ids: string[]): Promise<BulkApproveResponse> {
    return this.api
      .post('v2/payments/bulk/approve', {
        data: { transaction_ids },
      })
      .then((res) => res.data);
  }

  async batch(data: PaymentBatchUpdateSchema): Promise<Payment[]> {
    return this.api
      .post(this.path('batch'), {
        data,
      })
      .then((res) => {
        return res.data.map((p) => Payment.fromJson(p));
      });
  }

  async export(
    options: {
      format?: 'csv' | 'json';
      payment?: Payment;
      since?: string | undefined;
    } = { format: 'csv' },
  ): Promise<PaymentExportResponse> {
    const headers: Record<string, any> = {};
    const format = options.format || 'csv';
    const responseType = format === 'json' ? 'application/json' : 'text/csv';
    headers.Accept = responseType;

    return this.api
      .post(this.path(options.payment?.id || '', 'export'), {
        headers,
        responseType: 'blob',
        data: { since: options?.since },
      })
      .then((res) => {
        return {
          blob: res.data,
          filename: `payment${options.payment?.id ? '-' + options.payment.id : 's'}.${options.format}`,
          contentType: responseType,
        };
      });
  }
}
